import React, { useEffect, useMemo, useState } from 'react'
import { cloneDeep } from 'lodash'

import Button from 'components_new/atoms/Button'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'

import DndList from 'components_new/molecules/DndList'
import Text from 'components_new/atoms/Text'

import {
  ParsedDashboardFilterOptions,
  TDashboardFilterOption
} from 'types/GlobalDashboardFilter'
import SelectSearch from 'components_new/molecules/SelectSearch'
import Tooltip from 'components_new/atoms/Tooltip'
import { useTranslation } from 'language/translator'

interface EditUserFiltersDialogProps {
  availableDashboardFilterOptions: TDashboardFilterOption[]
  dashboardFilterOptions: Map<string, ParsedDashboardFilterOptions>
  onClose: () => void
  onSave: (
    dashboardFilterOptions: {
      index: number
      attribute_id: string
    }[]
  ) => void
  open: boolean
}

const EditUserFiltersDialog = (props: EditUserFiltersDialogProps) => {
  const {
    availableDashboardFilterOptions,
    dashboardFilterOptions,
    onClose,
    onSave,
    open
  } = props

  const { t } = useTranslation()

  const [editedFilters, setEditedFilters] = useState<
    ParsedDashboardFilterOptions[]
  >([])

  useEffect(() => {
    setEditedFilters(cloneDeep(Array.from(dashboardFilterOptions.values())))
  }, [open])

  const dndItems = useMemo(() => {
    return editedFilters
      .sort((a, b) => a.index - b.index)
      .map((filter) => {
        return {
          id: filter.attribute_id,
          content: (
            <Box
              key={filter.attribute_id}
              sx={{
                px: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Text
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                variant="subtitle2"
              >
                {filter.title}
              </Text>
              <Tooltip title={t('global', 'delete')}>
                <IconButton
                  onClick={() => handleToggleHidden(filter.attribute_id)}
                  size="small"
                >
                  <Icon
                    color={'disabled'}
                    fontSize="small"
                    name={'RemoveCircleOutlineOutlined'}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          ),
          containerSx: {
            mb: 1,
            p: 1
          }
        }
      })
  }, [editedFilters])

  const handleToggleHidden = (id: string) => {
    setEditedFilters(
      editedFilters.filter((filter) => filter.attribute_id !== id)
    )
  }

  return (
    <SimpleDialog
      title={t('filters', 'edit_filters')}
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            {t('global', 'cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onSave(
                editedFilters
                  .sort((a, b) => a.index - b.index)
                  .map((filter, i) => {
                    return {
                      attribute_id: filter.attribute_id,
                      index: i
                    }
                  })
              )
              onClose()
            }}
          >
            {t('global', 'save')}
          </Button>
        </>
      }
    >
      <SelectSearch
        closeOnSelect={true}
        disableClearable={true}
        label={t('filters', 'add_filter')}
        onChange={(option) => {
          const newFilter = availableDashboardFilterOptions.find(
            (opt) => opt.attribute_id === option.id
          ) as TDashboardFilterOption

          setEditedFilters([
            ...editedFilters,
            { ...newFilter, index: editedFilters.length, options: undefined }
          ])
        }}
        multiple={false}
        options={availableDashboardFilterOptions
          .filter(
            (opt) =>
              !editedFilters.find(
                (filter) => filter.attribute_id === opt.attribute_id
              )
          )
          .map((filter) => ({
            id: filter.attribute_id,
            title: filter.title
          }))}
        selected={null}
        sort={true}
        sx={{ mb: 2 }}
      />
      <DndList
        editable={true}
        handleDragEnd={(newOrder) => {
          setEditedFilters((prevFilters) => {
            return newOrder.map((id, index) => {
              const filter = prevFilters.find(
                (filter) => filter.attribute_id === id
              ) as ParsedDashboardFilterOptions

              return {
                ...filter,
                index
              }
            })
          })
        }}
        itemGap={0}
        itemHeight={40}
        items={dndItems}
      />
    </SimpleDialog>
  )
}

export default EditUserFiltersDialog
